/**
 * Every issue tracker's description, and the words screens build from it
 *.
 *
 * The server describes each tracker it can import from: its name, logo,
 * what it calls a project and an issue type, its filter language and its
 * sign-in fields. Screens that name a tracker read it from here instead of
 * typing "Jira" in, so a new tracker appears everywhere without a frontend
 * change. The list is the product's own static data, so it never goes stale
 * within a session.
 *
 * Built like `usePublicSettings` and `useRunLimits`: one request shared by
 * everyone who mounts at once, cached for the session, and a failed request
 * dropped from the cache so a retry asks again.
 *
 * Lives in the shared hooks folder because three screens use it: the
 * workspace connect card, the app's issue-source card and the suggested-test
 * badge.
 */
import { useCallback, useEffect, useState } from 'react'
import { issueTrackersAPI, type IssueTrackerProviderDescriptor, type IssueTrackerSignIn } from '@/lib/api'

/** The in-flight or settled request, shared by every caller. */
let inFlight: Promise<IssueTrackerProviderDescriptor[]> | null = null
/** The settled list, so a later mount renders it on its first paint. */
let settled: IssueTrackerProviderDescriptor[] | undefined

function fetchDescriptors(): Promise<IssueTrackerProviderDescriptor[]> {
  if (!inFlight) {
    inFlight = Promise.resolve()
      .then(() => issueTrackersAPI.listProviders())
      .then((list) => {
        settled = list
        return list
      })
      .catch((err) => {
        // Drop the failure so the next mount asks again.
        inFlight = null
        throw err
      })
  }
  return inFlight
}

/** Forget the cached list, for tests. */
export function invalidateTrackerDescriptors(): void {
  inFlight = null
  settled = undefined
}

/** The list, plus whether loading it failed and a way to ask again. For a
 * screen that must tell "still loading" from "couldn't load", like the
 * settings card, which would otherwise treat a connected tracker as unknown. */
export interface TrackerDescriptorsState {
  descriptors: IssueTrackerProviderDescriptor[] | undefined
  failed: boolean
  retry: () => void
}

export function useTrackerDescriptorsState(): TrackerDescriptorsState {
  const [descriptors, setDescriptors] = useState<IssueTrackerProviderDescriptor[] | undefined>(settled)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const retry = useCallback(() => {
    setFailed(false)
    setAttempt((n) => n + 1)
  }, [])
  useEffect(() => {
    if (descriptors) return
    let cancelled = false
    fetchDescriptors()
      .then((l) => { if (!cancelled) setDescriptors(l) })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [descriptors, attempt])
  return { descriptors, failed, retry }
}

/** The list alone, for screens that only label things. `undefined` while it
 * loads or after a failed load; callers fall back through `descriptorFor`,
 * and the next mount asks again. */
export function useTrackerDescriptors(): IssueTrackerProviderDescriptor[] | undefined {
  return useTrackerDescriptorsState().descriptors
}

/** The description for one tracker. Until the list loads, or for a tracker
 * the server no longer describes, a stand-in built from the tracker's id
 * keeps the screen readable: its id as the name, a plain logo, and the
 * generic words "project" and "issue type". */
export function descriptorFor(
  descriptors: IssueTrackerProviderDescriptor[] | undefined,
  provider: string | undefined,
): IssueTrackerProviderDescriptor {
  const found = provider ? descriptors?.find((d) => d.provider === provider) : undefined
  if (found) return found
  const id = provider ?? ''
  return {
    provider: id,
    displayName: id ? id.charAt(0).toUpperCase() + id.slice(1) : 'Issue tracker',
    logoKey: id,
    projectNoun: 'project',
    issueTypeNoun: 'issue type',
    capabilities: { inboundIssues: true },
    classificationScheme: 'issueType',
    credentialSchema: [],
    pagination: 'cursor',
  }
}

/** How a connection signs in, matching the server's reading: a tracker with
 * an app install uses it unless the connection saved `'token'`; one without
 * signs in with its typed-in fields. */
export function signInOf(
  d: Pick<IssueTrackerProviderDescriptor, 'credentialSource'> | undefined,
  connection: { signIn?: IssueTrackerSignIn } | null | undefined,
): IssueTrackerSignIn {
  if (!d?.credentialSource) return 'token'
  return connection?.signIn ?? 'app'
}

/** Whether a tracker lets the admin pick between its app install and a
 * typed-in token. */
export function offersSignInChoice(d: Pick<IssueTrackerProviderDescriptor, 'credentialSource' | 'credentialSchema'>): boolean {
  return Boolean(d.credentialSource) && d.credentialSchema.length > 0
}

/** What a connection to the tracker is called on screen: "GitHub Issues"
 * for GitHub, whose name alone covers more than its issues; otherwise the
 * tracker's own name ("Jira"). Messages about the tracker itself still use
 * `displayName`. */
export function connectionNameOf(d: Pick<IssueTrackerProviderDescriptor, 'displayName' | 'connectionName'>): string {
  return d.connectionName ?? d.displayName
}

/** "project" → "projects", "repository" → "repositories". */
function plural(noun: string): string {
  return /[^aeiou]y$/i.test(noun) ? `${noun.slice(0, -1)}ies` : `${noun}s`
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** The tracker's words in the forms screens need. */
export interface TrackerWords {
  /** "project" */
  project: string
  /** "projects" */
  projects: string
  /** "Projects" */
  Projects: string
  /** "issue type" */
  issueType: string
  /** "issue types" */
  issueTypes: string
}

export function trackerWords(d: Pick<IssueTrackerProviderDescriptor, 'projectNoun' | 'issueTypeNoun'>): TrackerWords {
  const projects = plural(d.projectNoun)
  return {
    project: d.projectNoun,
    projects,
    Projects: capitalize(projects),
    issueType: d.issueTypeNoun,
    issueTypes: plural(d.issueTypeNoun),
  }
}
